import React, { useState } from 'react';
import { Shield, CheckCircle, X, Lock, ThumbsUp, ThumbsDown } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import './AdminPages.css';

function approvalList(req) {
  return req.approvedByUids || req.approvedBy || [];
}

export default function ApprovalsPage() {
  const { data, commitMany, showToast } = useApp();
  const { isFounder, user } = useAuth();

  if (!isFounder) {
    return (
      <div className="admin-page">
        <div className="page-header"><div><h1 className="page-title">Approvals</h1></div></div>
        <div className="perm-denied">
          <div className="perm-denied-icon"><Lock size={26} style={{ color:'var(--text-dim)' }}/></div>
          <p style={{ fontWeight:700 }}>Founders Only</p>
          <p style={{ color:'var(--text-muted)',fontSize:'0.875rem' }}>Only founders (super admins) can approve or reject these requests.</p>
        </div>
      </div>
    );
  }

  const [filter, setFilter] = useState('Pending');

  const otherFounder = data.employees.find(e => e.role === 'super_admin' && e.authUid && e.authUid !== user.uid);

  const approve = async (req) => {
    const list = approvalList(req);
    const alreadyApproved = list.includes(user.uid);
    if (alreadyApproved) { showToast('You have already approved this request', 'warning'); return; }
    const newApprovedBy = [...list, user.uid];
    const newStatus = newApprovedBy.length >= 2 ? 'Approved' : 'Partial';
    try {
      await commitMany({
        approvalRequests: data.approvalRequests.map(r =>
          r.id === req.id ? { ...r, approvedByUids: newApprovedBy, approvedBy: newApprovedBy, status: newStatus } : r
        ),
        invoices: data.invoices.map(i =>
          i.id === req.ref ? { ...i, approvals: newApprovedBy.length } : i
        ),
        quotations: (data.quotations || []).map(q =>
          q.id === req.ref ? { ...q, approvals: newApprovedBy.length, status: newStatus === 'Approved' ? 'Approved' : 'Pending Approval' } : q
        ),
      });
      showToast(newStatus === 'Approved' ? `${req.ref} fully approved — requester emailed when mail is configured` : `Your approval recorded. Waiting for second founder.`);
    } catch {}
  };

  const reject = async (req) => {
    if (!window.confirm('Reject this request?')) return;
    try {
      await commitMany({
        approvalRequests: data.approvalRequests.map(r =>
          r.id === req.id ? { ...r, status: 'Rejected', approvedByUids: [], approvedBy: [] } : r
        ),
        quotations: (data.quotations || []).map(q =>
          q.id === req.ref ? { ...q, approvals: 0, status: 'Draft', sentToClient: false } : q
        ),
        invoices: data.invoices.map(i =>
          i.id === req.ref ? { ...i, approvals: 0 } : i
        ),
      });
      showToast(`${req.ref} rejected — requester notified by email when mail is configured`, 'warning');
    } catch {}
  };

  const TYPE_BADGE = { Invoice:'badge-gold', Quotation:'badge-info', Contract:'badge-purple' };
  const STATUS_BADGE = { Pending:'badge-warning', Partial:'badge-info', Approved:'badge-success', Rejected:'badge-danger' };

  const filtered = data.approvalRequests.filter(r => filter === 'All' || r.status === filter);
  const pending = data.approvalRequests.filter(r => r.status === 'Pending' || r.status === 'Partial').length;

  return (
    <div className="admin-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Dual-Founder Approvals</h1>
          <p className="page-subtitle">Critical actions requiring both founders' sign-off · {pending} pending</p>
        </div>
      </div>

      <div className="info-banner info-banner-gold" style={{ marginBottom:20 }}>
        <Shield size={16}/>
        <span>Both founders must approve invoices, quotations, and contracts before they can be issued. Each founder approves independently (tracked by Firebase Auth UID).</span>
      </div>

      <div className="filter-tabs" style={{ marginBottom:20,width:'fit-content' }}>
        {['Pending','Partial','Approved','Rejected','All'].map(f=>(
          <button key={f} className={`filter-tab ${filter===f?'active':''}`} onClick={()=>setFilter(f)}>{f}</button>
        ))}
      </div>

      <div style={{ display:'flex',flexDirection:'column',gap:14 }}>
        {filtered.map(req => {
          const list = approvalList(req);
          const myApproval = list.includes(user.uid);
          const otherApproval = otherFounder?.authUid ? list.includes(otherFounder.authUid) : false;
          const otherLabel = otherFounder
            ? `${otherFounder.name.split(' ')[0]} ${(otherFounder.name.split(' ').slice(-1)[0] || '').charAt(0)}.`
            : 'Co-founder';

          return (
            <div key={req.id} className="approval-card">
              <div style={{ display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:16 }}>
                <div style={{ display:'flex',alignItems:'flex-start',gap:14 }}>
                  <div className="stat-icon"><Shield size={18}/></div>
                  <div>
                    <div style={{ display:'flex',alignItems:'center',gap:8,marginBottom:4 }}>
                      <span className={`badge ${TYPE_BADGE[req.type]||'badge-gray'}`}>{req.type}</span>
                      <span style={{ fontFamily:'var(--font-mono)',fontWeight:700,color:'var(--secondary-color)',fontSize:'0.85rem' }}>{req.ref}</span>
                      <span className={`badge ${STATUS_BADGE[req.status]||'badge-gray'}`}>{req.status}</span>
                    </div>
                    <p style={{ fontWeight:700,fontSize:'0.9rem',marginBottom:3 }}>
                      {req.client}{req.amount>0 && <span style={{ color:'var(--secondary-color)',marginLeft:8,fontFamily:'var(--font-mono)' }}>R {req.amount.toLocaleString()}</span>}
                    </p>
                    <p style={{ fontSize:'0.78rem',color:'var(--text-muted)' }}>Requested by {req.requestedByName} · {new Date(req.date).toLocaleDateString('en-ZA')}</p>

                    <div className="approval-dots">
                      <div className={`approval-dot ${myApproval?'filled':'empty'}`}>{myApproval?'✓':'?'}</div>
                      <span style={{ fontSize:'0.72rem',color:'var(--text-muted)',margin:'0 2px' }}>You</span>
                      <div className={`approval-dot ${otherApproval?'filled':'empty'}`}>{otherApproval?'✓':'?'}</div>
                      <span style={{ fontSize:'0.72rem',color:'var(--text-muted)',margin:'0 2px' }}>{otherLabel}</span>
                      <span style={{ fontSize:'0.72rem',color:'var(--text-dim)',marginLeft:4 }}>{list.length}/2 approved</span>
                    </div>
                  </div>
                </div>

                {(req.status === 'Pending' || req.status === 'Partial') && (
                  <div style={{ display:'flex',gap:8,flexShrink:0 }}>
                    {!myApproval ? (
                      <>
                        <button className="btn-gold" style={{ display:'flex',alignItems:'center',gap:6,padding:'8px 14px',fontSize:'0.82rem' }} onClick={()=>approve(req)}>
                          <ThumbsUp size={13}/> Approve
                        </button>
                        <button className="btn-ghost" style={{ display:'flex',alignItems:'center',gap:6,padding:'8px 14px',fontSize:'0.82rem',color:'var(--danger)' }} onClick={()=>reject(req)}>
                          <ThumbsDown size={13}/> Reject
                        </button>
                      </>
                    ) : (
                      <div style={{ display:'flex',alignItems:'center',gap:6,fontSize:'0.82rem',color:'var(--success)',padding:'8px 14px',background:'rgba(56,161,105,0.1)',border:'1px solid rgba(56,161,105,0.3)',borderRadius:'var(--radius-md)' }}>
                        <CheckCircle size={13}/> You approved
                      </div>
                    )}
                  </div>
                )}
                {req.status === 'Approved' && (
                  <div style={{ display:'flex',alignItems:'center',gap:6,fontSize:'0.82rem',color:'var(--success)',padding:'8px 14px',background:'rgba(56,161,105,0.1)',border:'1px solid rgba(56,161,105,0.3)',borderRadius:'var(--radius-md)' }}>
                    <CheckCircle size={13}/> Fully Approved
                  </div>
                )}
                {req.status === 'Rejected' && (
                  <div style={{ display:'flex',alignItems:'center',gap:6,fontSize:'0.82rem',color:'var(--danger)',padding:'8px 14px',background:'rgba(229,62,62,0.1)',border:'1px solid rgba(229,62,62,0.3)',borderRadius:'var(--radius-md)' }}>
                    <X size={13}/> Rejected
                  </div>
                )}
              </div>
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div style={{ textAlign:'center',padding:'48px 20px',color:'var(--text-dim)' }}>
            <CheckCircle size={36} style={{ marginBottom:12,opacity:0.4 }}/>
            <p>No {filter.toLowerCase()} approvals</p>
          </div>
        )}
      </div>
    </div>
  );
}
